'use client';

import React, { useEffect } from 'react';
import Link from 'next/link';
import { cinzel } from '@/lib/fonts';

const posts = [1, 2, 3, 4, 5, 6].map((n) => ({
    video: `/videos/our-work/d1s/reel-${n}.mp4`,
    poster: `/images/our-work/d1s-post${n}.webp`,
    label: `Design1Studio Instagram reel ${n}`,
}));

export default function Design1StudioSMMPage() {
    useEffect(() => {
        document.title = 'Design1Studio — Social Media | Lead Workz';
    }, []);

    return (
        <div className={cinzel.variable}>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap');
                @media (max-width: 768px) {
                    .d1s-grid { grid-template-columns: 1fr 1fr !important; gap: 12px !important; }
                    .d1s-follow-row { flex-direction: column !important; align-items: center !important; text-align: center !important; }
                    .d1s-follow-info { align-items: center !important; }
                    .d1s-follow-btn { align-self: center !important; }
                    .d1s-card { max-width: calc(100% - 32px) !important; border-radius: 40px !important; }
                    .d1s-hero { padding: 48px 24px 40px !important; }
                    .d1s-hero-title { font-size: clamp(28px, 8vw, 56px) !important; }
                    .d1s-section { padding: 40px 20px !important; }
                }
            `}</style>

            <main style={{ background: '#080400', minHeight: '100vh', paddingBottom: 80 }}>
                <div style={{ height: 112 }} />

                {/* ── White rounded card ── */}
                <div className="d1s-card" style={{
                    background: '#fbfdfd',
                    borderRadius: 100,
                    width: 1798,
                    maxWidth: 'calc(100% - 40px)',
                    margin: '0 auto',
                    overflow: 'hidden',
                }}>

                    {/* ── Hero: Brand Logo section ── */}
                    <div className="d1s-hero" style={{
                        background: '#402419',
                        padding: 'clamp(48px, 7vw, 104px) clamp(32px, 7vw, 126px)',
                        boxSizing: 'border-box',
                    }}>
                        <div style={{ maxWidth: 1547, margin: '0 auto' }}>
                            <h1 className="d1s-hero-title" style={{
                                fontFamily: 'var(--font-cinzel), Cinzel, serif',
                                fontWeight: 700,
                                fontSize: 'clamp(32px, 5vw, 70px)',
                                letterSpacing: '0.13em',
                                color: '#e9dfd0',
                                margin: '0 0 24px',
                                lineHeight: 1.1,
                            }}>
                                SOCIAL{' '}
                                <span style={{
                                    fontFamily: '"Great Vibes", cursive',
                                    fontWeight: 400,
                                    color: '#e0c58f',
                                    fontStyle: 'italic',
                                }}>
                                    Media Strategy
                                </span>
                            </h1>
                            <div style={{
                                fontFamily: 'var(--font-manrope), Manrope, sans-serif',
                                fontWeight: 400,
                                fontSize: 'clamp(14px, 1.3vw, 22px)',
                                color: '#f5fde9',
                                letterSpacing: '0.07em',
                                lineHeight: 1.75,
                                maxWidth: 1400,
                            }}>
                                <p style={{ margin: '0 0 12px' }}>For Design1Studio, our objective was to build a social media presence that showcased design expertise, inspired potential homeowners, and generated consistent brand visibility. Rather than simply publishing project images, we developed a content strategy that positioned the studio as a trusted authority in modern interior design.</p>
                                <p style={{ margin: '0 0 12px' }}>Our team created a balanced mix of project showcases, design inspiration, educational content, and behind-the-scenes insights to engage audiences at every stage of their decision-making journey. Every post was designed to reflect the studio's premium aesthetic while maintaining a cohesive and recognizable visual identity across all social platforms.</p>
                                <p style={{ margin: '0 0 12px' }}>To strengthen organic discoverability, we incorporated SEO-driven content planning by aligning posts with high-intent interior design search trends, location-focused keywords, optimized captions, and strategic hashtag usage. This approach helped improve visibility across both social platforms and search engines, allowing Design1Studio to reach a broader audience actively searching for interior design solutions.</p>
                                <p style={{ margin: 0 }}>We continuously analyzed content performance, audience engagement, and platform insights to refine the strategy over time. The result was a stronger digital presence that increased brand awareness, reinforced credibility, and positioned Design1Studio as a leading choice for premium residential interior design.</p>
                            </div>
                        </div>
                    </div>

                    {/* ── 6-post Instagram grid ── */}
                    <div className="d1s-section" style={{
                        padding: 'clamp(40px, 5vw, 80px) clamp(24px, 5vw, 87px)',
                        boxSizing: 'border-box',
                    }}>
                        <div className="d1s-grid" style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 'clamp(10px, 2vw, 32px)',
                            maxWidth: 1100,
                            margin: '0 auto',
                        }}>
                            {posts.map((post, i) => (
                                <div key={i} style={{
                                    aspectRatio: '510 / 780',
                                    borderRadius: 'clamp(8px, 1.2vw, 16px)',
                                    overflow: 'hidden',
                                    background: '#e8e5df',
                                    position: 'relative',
                                }}>
                                    <video
                                        src={post.video}
                                        poster={post.poster}
                                        aria-label={post.label}
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        preload="metadata"
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'cover',
                                            objectPosition: 'top center',
                                            display: 'block',
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ── Follow on Instagram ── */}
                    <div style={{
                        padding: 'clamp(40px, 5vw, 80px) clamp(24px, 5vw, 87px) clamp(60px, 6vw, 100px)',
                        boxSizing: 'border-box',
                        textAlign: 'center',
                    }}>
                        {/* Heading */}
                        <h2 style={{
                            fontFamily: 'var(--font-cinzel), Cinzel, serif',
                            fontWeight: 400,
                            fontSize: 'clamp(26px, 7vw, 50px)',
                            letterSpacing: '0.05em',
                            color: '#1f1f1f',
                            margin: '0 0 clamp(32px, 4vw, 60px)',
                            textTransform: 'capitalize',
                        }}>
                            Follow Us On{' '}
                            <span style={{
                                fontFamily: '"Great Vibes", cursive',
                                fontWeight: 400,
                                fontStyle: 'italic',
                                color: '#402419',
                                fontSize: 'clamp(32px, 9vw, 80px)',
                            }}>
                                Instagram
                            </span>
                        </h2>

                        {/* Profile row */}
                        <div className="d1s-follow-row" style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'clamp(24px, 4vw, 60px)',
                            maxWidth: 1238,
                            margin: '0 auto',
                        }}>
                            {/* Avatar circle */}
                            <div style={{
                                width: 'clamp(160px, 34vw, 333px)',
                                height: 'clamp(160px, 34vw, 333px)',
                                flexShrink: 0,
                                position: 'relative',
                                borderRadius: '50%',
                                overflow: 'hidden',
                                background: '#f0ebe3',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}>
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src="/images/our-work/d1s-avatar-circle.svg"
                                    alt="Design1Studio avatar"
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                                {/* eslint-disable-next-line @next/next/no-img-element */}
                                <img
                                    src="/images/our-work/d1s-logo.webp"
                                    alt="Design1Studio logo"
                                    style={{
                                        position: 'absolute',
                                        width: '65%',
                                        height: 'auto',
                                        objectFit: 'contain',
                                    }}
                                />
                            </div>

                            {/* Info */}
                            <div className="d1s-follow-info" style={{ textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 'clamp(16px, 2vw, 32px)' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                                    <p style={{
                                        fontFamily: '"Inter", sans-serif',
                                        fontWeight: 400,
                                        fontSize: 'clamp(22px, 5vw, 40px)',
                                        color: '#1f1f1f',
                                        margin: 0,
                                        textTransform: 'lowercase',
                                    }}>
                                        @design1studio_interiors
                                    </p>
                                    <p style={{
                                        fontFamily: '"Inter", sans-serif',
                                        fontWeight: 300,
                                        fontSize: 'clamp(16px, 3.2vw, 32px)',
                                        color: '#1f1f1f',
                                        margin: 0,
                                        textTransform: 'lowercase',
                                    }}>
                                        Design1studio | Interior | Design Experts in Bangalore
                                    </p>
                                </div>

                                {/* Follow button */}
                                <Link
                                    href="https://www.instagram.com/design1studio_interiors"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="d1s-follow-btn"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: '#402419',
                                        color: '#ffffff',
                                        fontFamily: '"Inter", sans-serif',
                                        fontWeight: 400,
                                        fontSize: 'clamp(16px, 3vw, 24px)',
                                        letterSpacing: '0.05em',
                                        textTransform: 'uppercase',
                                        padding: 'clamp(14px, 3vw, 22px) clamp(28px, 6vw, 56px)',
                                        borderRadius: 50,
                                        textDecoration: 'none',
                                        width: 'fit-content',
                                        transition: 'opacity 0.2s',
                                    }}
                                    onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = '0.85'; }}
                                    onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = '1'; }}
                                >
                                    Follow
                                </Link>
                            </div>
                        </div>
                    </div>

                </div>

                {/* ── Back link ── */}
                <div style={{ textAlign: 'center', marginTop: 60 }}>
                    <Link
                        href="/our-work/social-media-marketing"
                        style={{ fontFamily: 'var(--font-manrope)', fontWeight: 500, fontSize: 16, color: '#fbfdfd', opacity: 0.5, textDecoration: 'none', letterSpacing: '0.05em', transition: 'opacity 0.2s' }}
                        onMouseEnter={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = '1'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLAnchorElement).style.opacity = '0.5'; }}
                    >
                        ← Back to Social Media Marketing
                    </Link>
                </div>
            </main>
        </div>
    );
}
